import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Dialog, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import Toggle from 'components/Toggle';
import {
  SCORECARD_COMMON_GOAL_TYPE_CUSAT,
  SCORECARD_GOAL_TYPE_CUSAT,
  SCORECARD_GOAL_TYPE_METRIC,
} from 'constants/scorecards';
import { SelfAssessmentSetups } from 'helpers';
import { commonTexts, formTexts } from 'i18n';
import { usePagePermissions } from 'hooks';
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
  Section,
  SectionDescription,
  SectionTitle,
} from './GoalSettingsDialog.styled';
import { GoalSettingsValues } from './interfaces';

/**
 * Every section below is gated on either `!isMetricGoal` or `showPmOnly`, so a
 * metric goal on the owner's own scorecards page has nothing to show and the
 * dialog would open with an empty body. Callers gate their settings entry point
 * on this instead of offering that dead end.
 */
export function hasVisibleGoalSettings({
  goalType,
  isOwnScorecardsPage,
}: {
  goalType?: string;
  isOwnScorecardsPage: boolean;
}) {
  return goalType !== SCORECARD_GOAL_TYPE_METRIC || !isOwnScorecardsPage;
}

interface GoalSettingsDialogProps {
  goalName: string;
  goalType?: string;
  initialValues: GoalSettingsValues;
  /** Persistence is the caller's concern: the edit form defers it to its own
   * submit, while the inline-edit view saves immediately. */
  onSave: (values: GoalSettingsValues) => void;
  onClose: () => void;
  isSaving?: boolean;
}

export default function GoalSettingsDialog({
  goalName,
  goalType,
  initialValues,
  onSave,
  onClose,
  isSaving = false,
}: GoalSettingsDialogProps) {
  const intl = useIntl();
  const { isOwnScorecardsPage } = usePagePermissions();
  const isMetricGoal = goalType === SCORECARD_GOAL_TYPE_METRIC;
  const isCuSat =
    goalType === SCORECARD_GOAL_TYPE_CUSAT ||
    goalType === SCORECARD_COMMON_GOAL_TYPE_CUSAT;
  // OLD logic: hide PM-only toggles when editing one's own active scorecard
  const showPmOnly = !isOwnScorecardsPage;

  const [requestSelfAssessment, setRequestSelfAssessment] = useState(
    initialValues.selfAssessmentSetup === SelfAssessmentSetups.request,
  );
  const [allowScorecardComment, setAllowScorecardComment] = useState(
    Boolean(initialValues.allowScorecardComment),
  );
  const [allowScorecardOwnerEdits, setAllowScorecardOwnerEdits] = useState(
    Boolean(initialValues.allowScorecardOwnerEdits),
  );
  const [
    allowScorecardMetricPmOwnerEdits,
    setAllowScorecardMetricPmOwnerEdits,
  ] = useState(initialValues.allowScorecardMetricPmOwnerEdits ?? true);
  const [
    allowScorecardOwnerEditActualField,
    setAllowScorecardOwnerEditActualField,
  ] = useState(initialValues.allowScorecardOwnerEditActualField ?? true);
  const [autoAssignPmReviewer, setAutoAssignPmReviewer] = useState(
    Boolean(initialValues.autoAssignPmReviewer),
  );
  const [limitReviewersToEmployeeAndPm, setLimitReviewersToEmployeeAndPm] =
    useState(Boolean(initialValues.limitReviewersToEmployeeAndPm));

  const handleSave = () => {
    onSave({
      selfAssessmentSetup:
        requestSelfAssessment && !isMetricGoal
          ? SelfAssessmentSetups.request
          : SelfAssessmentSetups.disable,
      allowScorecardComment,
      allowScorecardOwnerEdits,
      allowScorecardMetricPmOwnerEdits,
      allowScorecardOwnerEditActualField,
      autoAssignPmReviewer,
      limitReviewersToEmployeeAndPm,
    });
  };

  const showAllowScorecardComment = !isCuSat && !isMetricGoal;
  const showAllowOwnerEdits = !isCuSat && !isMetricGoal && showPmOnly;
  const showMetricPmOwnerEdits = isMetricGoal && showPmOnly;
  const showAutoAssignPmReviewer = !isCuSat && !isMetricGoal && showPmOnly;
  const showLimitReviewersToEmployeeAndPm =
    !isCuSat && !isMetricGoal && showPmOnly;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogHeader>
        <span>
          <FormattedMessage
            id="pages.scorecards.edit.goalSettingsFor"
            defaultMessage="Goal settings for {goalName}"
            values={{ goalName }}
          />
        </span>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogHeader>
      <DialogBody>
        {!isMetricGoal && (
          <Section>
            <SectionTitle>
              <FormattedMessage {...formTexts.enableSelfAssessment} />
            </SectionTitle>
            <SectionDescription>
              <FormattedMessage {...formTexts.enableSelfAssessmentTooltip} />
            </SectionDescription>
            <Toggle
              checked={requestSelfAssessment}
              onChange={(e) => setRequestSelfAssessment(e.target.checked)}
              label={intl.formatMessage({
                id: 'pages.scorecards.edit.requestSelfAssessment',
                defaultMessage: 'Request self-assessment',
              })}
            />
          </Section>
        )}

        {showAllowScorecardComment && (
          <Section>
            <SectionTitle>
              <FormattedMessage {...formTexts.allowComment} />
            </SectionTitle>
            <SectionDescription>
              <FormattedMessage {...formTexts.allowCommentTooltip} />
            </SectionDescription>
            <Toggle
              checked={allowScorecardComment}
              onChange={(e) => setAllowScorecardComment(e.target.checked)}
              label={intl.formatMessage({
                id: 'pages.scorecards.edit.requestGoalComment',
                defaultMessage: 'Request goal comment',
              })}
            />
          </Section>
        )}

        {showAllowOwnerEdits && (
          <Section>
            <SectionTitle>
              <FormattedMessage {...formTexts.allowScorecardOwnerEdits} />
            </SectionTitle>
            <SectionDescription>
              <FormattedMessage
                {...formTexts.allowScorecardOwnerEditsTooltip}
              />
            </SectionDescription>
            <Toggle
              checked={allowScorecardOwnerEdits}
              onChange={(e) => setAllowScorecardOwnerEdits(e.target.checked)}
              label={intl.formatMessage(formTexts.allowScorecardOwnerEdits)}
            />
          </Section>
        )}

        {showMetricPmOwnerEdits && (
          <>
            <Section>
              <SectionTitle>
                <FormattedMessage
                  {...formTexts.allowScorecardMetricPmOwnerEdits}
                />
              </SectionTitle>
              <SectionDescription>
                <FormattedMessage
                  {...formTexts.allowScorecardMetricPmOwnerEditsTooltip}
                />
              </SectionDescription>
              <Toggle
                checked={allowScorecardMetricPmOwnerEdits}
                onChange={(e) =>
                  setAllowScorecardMetricPmOwnerEdits(e.target.checked)
                }
                label={intl.formatMessage(
                  formTexts.allowScorecardMetricPmOwnerEdits,
                )}
              />
            </Section>
            <Section>
              <SectionTitle>
                <FormattedMessage
                  {...formTexts.allowScorecardOwnerEditActualField}
                />
              </SectionTitle>
              <SectionDescription>
                <FormattedMessage
                  {...formTexts.allowScorecardOwnerEditActualFieldTooltip}
                />
              </SectionDescription>
              <Toggle
                checked={allowScorecardOwnerEditActualField}
                onChange={(e) =>
                  setAllowScorecardOwnerEditActualField(e.target.checked)
                }
                label={intl.formatMessage(
                  formTexts.allowScorecardOwnerEditActualField,
                )}
              />
            </Section>
          </>
        )}

        {showAutoAssignPmReviewer && (
          <Section>
            <SectionTitle>
              <FormattedMessage {...formTexts.autoAssignPmReviewer} />
            </SectionTitle>
            <SectionDescription>
              <FormattedMessage {...formTexts.autoAssignPmReviewerTooltip} />
            </SectionDescription>
            <Toggle
              checked={autoAssignPmReviewer}
              onChange={(e) => setAutoAssignPmReviewer(e.target.checked)}
              label={intl.formatMessage(formTexts.autoAssignPmReviewer)}
            />
          </Section>
        )}

        {showLimitReviewersToEmployeeAndPm && (
          <Section>
            <SectionTitle>
              <FormattedMessage {...formTexts.limitReviewersToEmployeeAndPm} />
            </SectionTitle>
            <SectionDescription>
              <FormattedMessage
                {...formTexts.limitReviewersToEmployeeAndPmTooltip}
              />
            </SectionDescription>
            <Toggle
              checked={limitReviewersToEmployeeAndPm}
              onChange={(e) =>
                setLimitReviewersToEmployeeAndPm(e.target.checked)
              }
              label={intl.formatMessage(
                formTexts.limitReviewersToEmployeeAndPm,
              )}
            />
          </Section>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="outlined" onClick={onClose} disabled={isSaving}>
          <FormattedMessage {...commonTexts.cancel} />
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          <FormattedMessage {...commonTexts.save} />
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
